import { MARKET_ORDER, marketFit } from "./_prospects_shared";
import { EntityLink } from "./_links";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import {
    BackToList,
    useAnnouncedSnackbar,
    useMasterDetail,
    workspaceListCls,
    workspaceRootCls,
} from "./_workspace";
import { useSidePanel } from "@rebasepro/cms";
import { publicJobUrl } from "../public_site";
import { employerDisplayName } from "../employer_identity";
import { ApprovePublishDialog } from "./ApprovePublishDialog";
import type { RebaseClient } from "@rebasepro/types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    CircularProgressCenter as RebaseCircularProgressCenter,
    FilterChip as RebaseFilterChip,
    IconButton as RebaseIconButton,
    SearchBar as RebaseSearchBar,
    Separator as RebaseSeparator,
    Tooltip as RebaseTooltip,
    Typography as RebaseTypography,
    CheckIcon as RebaseCheckIcon,
    ExternalLinkIcon as RebaseExternalLinkIcon,
    PencilIcon as RebasePencilIcon,
    RefreshCwIcon as RebaseRefreshCwIcon,
    UndoIcon as RebaseUndoIcon,
    cls,
    cardClickableMixin,
    cardSelectedMixin,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import { ThumbsDown as RebaseThumbsDown } from "lucide-react";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const FilterChip = RebaseFilterChip as React.ComponentType<any>;
const IconButton = RebaseIconButton as React.ComponentType<any>;
const SearchBar = RebaseSearchBar as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const Tooltip = RebaseTooltip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const CheckIcon = RebaseCheckIcon as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;
const PencilIcon = RebasePencilIcon as React.ComponentType<any>;
const RefreshCwIcon = RebaseRefreshCwIcon as React.ComponentType<any>;
const UndoIcon = RebaseUndoIcon as React.ComponentType<any>;
const ThumbsDown = RebaseThumbsDown as React.ComponentType<any>;

type ScrapedStatus = "pending" | "approved" | "rejected" | "expired";

/**
 * The offer a link asked for, read once from `?oferta=<id>`.
 *
 * This is the destination of the Discord message the scrape cron posts, which
 * is the only reason anyone opens this screen at a fixed moment: it says "these
 * eleven are waiting" and the point of clicking a line is to land on that line.
 * Without it the link could only open the queue and leave the reader to find
 * the offer again by title.
 *
 * Read at module scope on the first render rather than watched, because the
 * parameter describes how the screen was *entered*. Once the reader starts
 * moving through the queue it is stale, which is why `consume` clears it from
 * the address bar — leaving it there would make a later reload contradict
 * whatever they had selected.
 */
function consumeDeepLinkId(): string | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("oferta");
    if (!id) return null;
    params.delete("oferta");
    const query = params.toString();
    window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    );
    return id;
}

const TABS: { id: ScrapedStatus; label: string }[] = [
    { id: "pending", label: "Por revisar" },
    { id: "approved", label: "Aprobadas" },
    { id: "rejected", label: "Rechazadas" },
    { id: "expired", label: "Caducadas" }
];

/** What each filter holds, in one line, the way Correo and la cola do it. */
const TAB_HINT: Record<ScrapedStatus, string> = {
    pending: "Vacantes que encontramos publicadas en otras webs de empleo y nadie ha mirado. Aprobar una la copia al tablón de sustentalent.com.",
    approved: "Ya están publicadas en el tablón, con una copia en Vacantes.",
    rejected: "Las descartaste. Se guardan para no volver a proponértelas.",
    expired: "Pasó su fecha o el anuncio ya no está. No se publican."
};

/**
 * Who found the offer, by name rather than by package.
 *
 * "fuente: jsearch" is the id of an API we pay for. What the recruiter is
 * asking is where this came from and whether it can be trusted.
 */
const SOURCE_LABEL: Record<string, string> = {
    jsearch: "la encontramos en otra web (JSearch)",
    serpapi: "la encontramos en otra web (SerpAPI)",
    url_extract: "la importamos desde una URL",
    manual: "la cargó el equipo"
};

/**
 * The score, said as what it counts.
 *
 * `relevance_score` is keyword density: how much of the posting is
 * sustainability vocabulary. "relevancia 55%" reads as a verdict on the job,
 * which it is not — nothing here has read the job.
 */
function scoreChipLabel(score: number | null | undefined): string {
    return score === null || score === undefined
        ? "sin palabras de sostenibilidad"
        : `sostenibilidad ${Math.round(score * 100)}%`;
}

const SCORE_EXPLAINER =
    "Cuántas palabras de sostenibilidad aparecen en el anuncio. Lo cuenta el sistema al recogerla; no es una valoración del puesto.";

interface ScrapedRow {
    id: string;
    title?: string | null;
    company_name?: string | null;
    company_id?: string | null;
    description?: string | null;
    requirements?: string | null;
    location?: string | null;
    location_city?: string | null;
    location_country?: string | null;
    salary_range?: string | null;
    employment_type?: string | null;
    is_remote?: boolean | null;
    source?: string | null;
    employer_logo?: string | null;
    source_url?: string | null;
    application_link?: string | null;
    employer_website?: string | null;
    external_id?: string | null;
    relevance_score?: number | null;
    keywords_matched?: string[] | null;
    status?: ScrapedStatus | null;
    scraped_at?: string | null;
    posted_at?: string | null;
    promoted_job_id?: string | null;
}

function scoreColor(score: number | null | undefined): string {
    if (score === null || score === undefined) return "grayLighter";
    if (score >= 0.6) return "greenLighter";
    if (score >= 0.35) return "orangeLighter";
    return "redLighter";
}

/** "hace 3 días" — enough to spot an offer that has gone stale in the queue. */
function timeAgo(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const days = Math.floor((Date.now() - then) / 86_400_000);
    if (days <= 0) return "hoy";
    if (days === 1) return "ayer";
    if (days < 30) return `hace ${days} días`;
    const months = Math.round(days / 30);
    return months <= 1 ? "hace un mes" : `hace ${months} meses`;
}

/**
 * The employer logo the scraper captured, or the company's initial.
 *
 * These URLs are third-party and rot, so a dead one must not leave a broken
 * image in the queue. The failure is tracked by URL rather than as a boolean
 * because the detail pane reuses one instance across offers — a flag would
 * carry the previous logo's failure onto the next one.
 */
function CompanyLogo({ src, name, size = 40 }: {
    src?: string | null;
    name?: string | null;
    size?: number;
}) {
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    const usable = src && failedSrc !== src ? src : null;
    const initial = (name ?? "").trim().charAt(0).toUpperCase() || "?";

    return (
        <div
            style={{ width: size, height: size, minWidth: size }}
            className={cls(
                "shrink-0 rounded-lg overflow-hidden flex items-center justify-center border",
                defaultBorderMixin,
                // A logo is drawn for a light ground — a dark wordmark on a
                // dark plate is an empty square — so the plate stays white in
                // both themes, the way the company cover already draws it.
                // With no logo there is nothing to protect and the initial
                // reads better on the neutral tile: `surface-accent-800` was a
                // cold slate, the one tinted value left on the dark ladder.
                usable ? "bg-white" : "bg-surface-raised"
            )}
        >
            {usable
                ? (
                    <img
                        src={usable}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-contain p-1"
                        onError={() => setFailedSrc(src ?? null)}
                    />
                )
                : (
                    <span
                        className="font-medium text-text-primary dark:text-text-primary-dark"
                        style={{ fontSize: Math.round(size * 0.4) }}
                    >
                        {initial}
                    </span>
                )}
        </div>
    );
}

/**
 * The scraper's own `employment_type` vocabulary, in Spanish.
 *
 * Deliberately not the `jobs` enum: these are the raw values as scraped
 * (`full_time` on 40 of 42 rows), before drizzle/0026's split into hours and
 * contract type, and a scraped offer is evidence of what the source said rather
 * than a record we have cleaned. Translating on the way out keeps this screen
 * readable without pretending the underlying value is something it is not.
 */
const EMPLOYMENT_LABELS: Record<string, string> = {
    full_time: "jornada completa",
    part_time: "media jornada",
    contract: "por contrato",
    contractor: "por contrato",
    internship: "prácticas",
    temporary: "temporal",
    freelance: "freelance",
    volunteer: "voluntariado"
};

/**
 * Top-level "Ofertas por revisar" workspace.
 *
 * The scraper drops offers into `scraped_jobs` and something has to decide
 * whether each one belongs on the board. Doing that from the collection table
 * meant opening a row, reading raw scraped prose inside form fields, and then
 * remembering to flip a status — so 14 offers sat unreviewed. Here the source
 * text is laid out to be read, and approving is one button that runs the
 * existing LLM clean-and-publish endpoint.
 */
export function ScrapedReviewView() {
    const client = useRebaseClient<RebaseClient>();
    const sidePanel = useSidePanel();
    const { snackbar, liveRegion } = useAnnouncedSnackbar();
    const { openDetail, closeDetail, masterCls, detailCls } = useMasterDetail();

    const [rows, setRows] = useState<ScrapedRow[] | null>(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState<ScrapedStatus>("pending");
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [working, setWorking] = useState(false);
    // Held in a ref, not state: it is consumed exactly once, and re-reading it
    // on a later render would drag the reader back to where they arrived.
    //
    // `undefined` means "not read yet" and is what distinguishes the first
    // render from every later one — `useRef(consumeDeepLinkId())` would run the
    // side effect on every render and throw the result away.
    const pendingDeepLinkId = useRef<string | null | undefined>(undefined);
    if (pendingDeepLinkId.current === undefined) {
        pendingDeepLinkId.current = consumeDeepLinkId();
    }

    const load = useCallback(async () => {
        if (!client?.data) return;
        setLoading(true);
        setError("");
        try {
            const res = await (client.data as any).collection("scraped-jobs").find({
                limit: 500,
                orderBy: ["scraped_at", "desc"]
            });
            setRows(res.data ?? []);
        } catch (err: any) {
            setError(err?.message || "No se pudieron cargar las ofertas");
        } finally {
            setLoading(false);
        }
    }, [client]);

    useEffect(() => {
        void load();
    }, [load]);

    const counts = useMemo(() => {
        const c: Record<ScrapedStatus, number> = { pending: 0, approved: 0, rejected: 0, expired: 0 };
        for (const r of rows ?? []) {
            const s = (r.status ?? "pending") as ScrapedStatus;
            if (s in c) c[s] += 1;
        }
        return c;
    }, [rows]);

    const visible = useMemo(() => {
        if (!rows) return [];
        const term = search.trim().toLowerCase();
        return rows
            .filter(r => (r.status ?? "pending") === tab)
            .filter(r => !term || `${r.title ?? ""} ${r.company_name ?? ""}`.toLowerCase().includes(term))
            // Highest relevance first: the scraper's own score is the only
            // ranking signal available before a human reads the offer.
            .sort((a, b) => {
                // Market first: an offer in Spain or Latin America is one the
                // board is for; the same offer in Massachusetts is not, and
                // on 2026-09-06 every one of 186 rows carried the same
                // "relevancia 55%", so the score alone put them in no order.
                // Then the score, then the newest.
                const byMarket = MARKET_ORDER[marketFit(a.location_country ? [a.location_country] : [])]
                    - MARKET_ORDER[marketFit(b.location_country ? [b.location_country] : [])];
                if (byMarket !== 0) return byMarket;
                const byScore = (b.relevance_score ?? 0) - (a.relevance_score ?? 0);
                if (byScore !== 0) return byScore;
                return (b.scraped_at ?? "").localeCompare(a.scraped_at ?? "");
            });
    }, [rows, tab, search]);

    // Keep the selection on something visible, so the detail pane is never
    // stranded on a row the current tab no longer contains.
    useEffect(() => {
        // Not while a deep link is still waiting for its rows. Declaration
        // order is what makes this work: both effects run on the commit where
        // the rows land, this one first, and without the guard it would select
        // the top of the queue *after* the deep link had chosen its row —
        // same setter, last write wins.
        if (pendingDeepLinkId.current) return;
        if (visible.length === 0) {
            setSelectedId(null);
            return;
        }
        if (!selectedId || !visible.some(r => r.id === selectedId)) {
            setSelectedId(visible[0].id);
        }
    }, [visible, selectedId]);

    // Land on the offer a link named, once the rows it has to be found among
    // have arrived.
    //
    // Switches the tab as well as the selection: an offer reached from Discord
    // has often been decided by someone else in the meantime, and "Por revisar"
    // would not contain it. Both are set together, so the clamp above never
    // sees a selection outside the current tab.
    useEffect(() => {
        const wanted = pendingDeepLinkId.current;
        if (!wanted || !rows) return;
        pendingDeepLinkId.current = null;
        const row = rows.find(r => r.id === wanted);
        // A link to an offer that no longer exists — cleared by
        // `clear-scraped`, most likely — leaves the queue as it was rather than
        // emptying the screen.
        if (!row) return;
        setTab((row.status ?? "pending") as ScrapedStatus);
        setSelectedId(row.id);
    }, [rows]);

    const selected = rows?.find(r => r.id === selectedId) ?? null;

    /**
     * Move through the queue from the keyboard.
     *
     * Reviewing is a two-pane read-then-decide loop, and reaching for the mouse
     * to advance one row is most of the cost of doing fourteen of them. Only
     * navigation is bound: approving runs an LLM pass and publishes to the
     * public board, which is not something a stray keypress should trigger.
     */
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.metaKey || event.ctrlKey || event.altKey) return;
            // Never steal a keystroke aimed at the search box or any other field.
            const target = event.target as HTMLElement | null;
            if (target?.isContentEditable) return;
            if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;

            const step = event.key === "ArrowDown" || event.key === "j" ? 1
                : event.key === "ArrowUp" || event.key === "k" ? -1
                    : 0;
            if (step === 0 || visible.length === 0) return;
            event.preventDefault();

            const current = visible.findIndex(r => r.id === selectedId);
            const next = current === -1
                ? 0
                : Math.min(visible.length - 1, Math.max(0, current + step));
            const nextId = visible[next].id;
            setSelectedId(nextId);
            // The row is already in the DOM whether or not it is selected, so
            // this can run before React re-renders. Ids are UUIDs, so the
            // attribute selector needs no escaping.
            document
                .querySelector(`[data-offer-id="${nextId}"]`)
                ?.scrollIntoView({ block: "nearest" });
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [visible, selectedId]);

    /** Reject / expire are plain status writes; approving is not (see below). */
    const setStatus = useCallback(async (row: ScrapedRow, status: ScrapedStatus) => {
        if (!client?.data) return;
        setWorking(true);
        try {
            await (client.data as any).collection("scraped-jobs").update(row.id, {
                status,
                reviewed_at: new Date().toISOString()
            });
            setRows(rs => rs?.map(r => (r.id === row.id ? { ...r, status } : r)) ?? rs);
            snackbar.open({
                type: "success",
                // The tab's own wording, not the raw enum id: "→ expired" in an
                // otherwise Spanish screen reads as an error message.
                message: `"${row.title || "Oferta"}" → ${TABS.find(t => t.id === status)?.label ?? status}`
            });
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo actualizar" });
        } finally {
            setWorking(false);
        }
    }, [client, snackbar]);

    /**
     * Approving is a backend operation, not a status flip: the server runs the
     * offer through the LLM cleaner, creates the `jobs` row and links it back
     * via `promoted_job_id`. Doing it from here by writing `status = approved`
     * would mark the offer handled without ever publishing it.
     *
     * It also takes roughly half a minute, and it used to spend that time with
     * the toolbar greyed out and nothing else to show for it — the offer was
     * simply live, or not, when the buttons came back. So the click opens the
     * preview dialog instead: same pipeline, shown before it is committed.
     */
    const [approving, setApproving] = useState<ScrapedRow | null>(null);

    const onPublished = useCallback((row: ScrapedRow, body: { promoted_job_id?: string; message?: string }) => {
        setRows(rs => rs?.map(r =>
            r.id === row.id
                ? { ...r, status: "approved" as ScrapedStatus, promoted_job_id: body.promoted_job_id }
                : r) ?? rs);
        snackbar.open({
            type: "success",
            title: "Publicada",
            message: body?.message || `"${row.title}" está en el tablón`
        });
    }, [snackbar]);

    return (
        <div className={workspaceRootCls}>
            {liveRegion}

            {/* ── Queue ── */}
            <div className={cls(masterCls, defaultBorderMixin)}>
                <div className="p-4 pb-2 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <Typography variant="subtitle1" component="h1" className="grow">Ofertas por revisar</Typography>
                        <Tooltip title="Recargar" asChild>
                            <IconButton size="smallest" aria-label="Recargar ofertas" onClick={load} disabled={loading} aria-busy={loading}>
                                <RefreshCwIcon size={iconSize.smallest} className={cls(loading && "animate-spin")} />
                            </IconButton>
                        </Tooltip>
                    </div>

                    <SearchBar
                        size="small"
                        placeholder="Buscar oferta o empresa"
                        onTextSearch={(term: string | undefined) => setSearch(term ?? "")}
                    />

                    <div className="flex flex-wrap gap-1.5">
                        {TABS.map(t => (
                            <FilterChip key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                                {t.label} · {counts[t.id]}
                            </FilterChip>
                        ))}
                    </div>

                    {/* What the open filter holds, where somebody would ask. */}
                    <Typography variant="body2" color="secondary">
                        {TAB_HINT[tab]}
                    </Typography>

                    {/* An unadvertised shortcut is one nobody uses. */}
                    <Typography variant="body2" color="secondary">
                        ↑ ↓ (o j / k) para moverte por la cola
                    </Typography>
                </div>

                <div className={workspaceListCls}>
                    {error && <Alert color="error">{error}</Alert>}
                    {rows === null && !error && <CircularProgressCenter />}
                    {rows !== null && visible.length === 0 && !error && (
                        <div className="flex items-center justify-center py-8">
                            <Typography variant="label" color="secondary">Nada aquí</Typography>
                        </div>
                    )}
                    {visible.map(row => (
                        <Card
                            key={row.id}
                            data-offer-id={row.id}
                            onClick={() => { setSelectedId(row.id); openDetail(); }}
                            className={cls(
                                "p-3 flex gap-3",
                                cardClickableMixin,
                                row.id === selectedId && cardSelectedMixin
                            )}
                        >
                            <CompanyLogo src={row.employer_logo} name={row.company_name} size={40} />

                            <div className="min-w-0 grow flex flex-col gap-1.5">
                                <Typography variant="subtitle2" component="span" className="line-clamp-2">
                                    {row.title || "(sin título)"}
                                </Typography>
                                <Typography variant="body2" color="secondary" className="truncate">
                                    {[employerDisplayName(row.company_name), row.location, timeAgo(row.scraped_at)]
                                        .filter(Boolean).join(" · ")}
                                </Typography>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <Chip size="smallest" colorScheme={scoreColor(row.relevance_score)}>
                                        {scoreChipLabel(row.relevance_score)}
                                    </Chip>
                                    {row.is_remote && (
                                        <Chip size="smallest" colorScheme="blueLighter">remoto</Chip>
                                    )}
                                    {/* No "sin empresa" warning here any more: an
                                        offer awaiting review is *meant* to have no
                                        company — one is resolved when it is
                                        approved. The chip fired on every pending
                                        row, which is noise, not a warning. */}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>

            {/* ── Detail: the offer as text a person can actually read, with the
                   decision pinned below it (see the action bar at the bottom) ── */}
            <div className={cls(detailCls, "flex flex-col h-full min-w-0 overflow-y-visible")}>
                {!selected && rows !== null && !error && (
                    <div className="flex items-center justify-center h-full">
                        <Typography variant="label" color="secondary">
                            Selecciona una oferta para revisarla
                        </Typography>
                    </div>
                )}

                {selected && (
                    <div className="flex-1 overflow-y-auto">
                    <BackToList label="Ofertas" onClick={closeDetail}/>
                    <div className="max-w-4xl mx-auto w-full p-6 flex flex-col gap-5">

                        <div className="flex items-start gap-3">
                            <CompanyLogo src={selected.employer_logo} name={selected.company_name} size={56} />
                            <div className="grow min-w-0">
                                <Typography variant="h5" component="h2">
                                    {selected.title || "(sin título)"}
                                </Typography>
                                {/* The employer opens when it is on record: an
                                    offer from a client reads differently from
                                    one from a stranger. */}
                                {selected.company_id && selected.company_name && (
                                    <Typography variant="body2">
                                        <EntityLink kind="company" id={selected.company_id}>{selected.company_name}</EntityLink>
                                    </Typography>
                                )}
                                <Typography variant="body2" color="secondary">
                                    {[
                                        selected.company_id ? null : employerDisplayName(selected.company_name),
                                        selected.location,
                                        // The scraper's raw enum, translated.
                                        // Printed as-is this line read
                                        // "Costa Rica · full_time · hace 5 días"
                                        // — the one English token on a Spanish
                                        // screen, and the only value here the
                                        // reader has to decode rather than read.
                                        EMPLOYMENT_LABELS[selected.employment_type ?? ""] ?? selected.employment_type,
                                        selected.is_remote ? "remoto" : null,
                                        timeAgo(selected.scraped_at)
                                    ].filter(Boolean).join(" · ") || "—"}
                                </Typography>
                            </div>
                            <Tooltip title="Abrir el registro completo" asChild>
                                <IconButton
                                    size="smallest"
                                    aria-label="Editar oferta"
                                    onClick={() => sidePanel.open({ path: "scraped-jobs", entityId: selected.id })}
                                >
                                    <PencilIcon size={iconSize.smallest} />
                                </IconButton>
                            </Tooltip>
                        </div>

                        {/* Provenance — is this worth trusting at all? */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            <Tooltip title={SCORE_EXPLAINER} asChild>
                                <Chip size="smallest" colorScheme={scoreColor(selected.relevance_score)}>
                                    {scoreChipLabel(selected.relevance_score)}
                                </Chip>
                            </Tooltip>
                            {selected.source && (
                                <Chip size="smallest">
                                    {SOURCE_LABEL[selected.source] ?? `la encontró ${selected.source}`}
                                </Chip>
                            )}
                            {selected.salary_range && <Chip size="smallest">{selected.salary_range}</Chip>}
                            {/* Only worth saying once the offer is live: before
                                approval there is deliberately no company, and
                                after it an unlinked vacancy means the employer
                                could not be resolved and the board will show a
                                letter instead of a logo. */}
                            {selected.status === "approved" && !selected.company_id && (
                                <Chip size="smallest" colorScheme="orangeLighter">
                                    no encontramos la ficha de esta empresa
                                </Chip>
                            )}
                            {/* Unlabelled, these six blue words looked like
                                tags somebody had put on the offer. They are
                                the words that made the scraping keep it. */}
                            {(selected.keywords_matched ?? []).length > 0 && (
                                <>
                                    <Typography variant="body2" color="secondary">coincide en:</Typography>
                                    {(selected.keywords_matched ?? []).slice(0, 6).map(k => (
                                        <Chip key={k} size="smallest" colorScheme="blueLighter">{k}</Chip>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* `component="a"` is required: Button renders whatever
                            `component` says and defaults to <button>, where an
                            href is inert. Without it these look like links and
                            do nothing. */}
                        <div className="flex flex-wrap gap-2">
                            {/* `application_link` first, and `source_url` only as a
                                fallback. JSearch's "source" for a Google Jobs
                                result is a search URL —
                                `google.com/search?q=jobs&…#vhid=…` — whose
                                fragment identifies a card inside a result set
                                that no longer exists by the time anyone clicks:
                                41 of 42 live offers carry one, so "Oferta
                                original" landed on Google every time. The apply
                                link is the posting itself (LinkedIn, the
                                employer's site, an ATS) and is present on the
                                same 41 rows, with not one Google URL among
                                them. */}
                            {(selected.application_link || selected.source_url) && (
                                <Button
                                    component="a"
                                    variant="text"
                                    size="small"
                                    href={selected.application_link || selected.source_url!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ExternalLinkIcon size={iconSize.smallest} /> Oferta original
                                </Button>
                            )}
                            {/* The scraper's own `source_url` is not offered.
                                For a Google Jobs result it is a search URL whose
                                fragment names a card in a result set that no
                                longer exists — a button that opened Google and
                                showed nothing, on every offer. "Oferta original"
                                above is the posting; nothing else is worth a
                                click. */}
                            {selected.employer_website && (
                                <Button
                                    component="a"
                                    variant="text"
                                    size="small"
                                    href={`https://${selected.employer_website.replace(/^https?:\/\//, "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ExternalLinkIcon size={iconSize.smallest} /> Web de la empresa
                                </Button>
                            )}
                        </div>

                        <Separator />

                        {selected.description && (
                            <section>
                                <Typography variant="subtitle2" component="h3" className="mb-1">Descripción</Typography>
                                <Typography variant="body1" color="secondary" className="whitespace-pre-wrap">
                                    {selected.description}
                                </Typography>
                            </section>
                        )}

                        {selected.requirements && (
                            <section>
                                <Typography variant="subtitle2" component="h3" className="mb-1">Requisitos</Typography>
                                <Typography variant="body1" color="secondary" className="whitespace-pre-wrap">
                                    {selected.requirements}
                                </Typography>
                            </section>
                        )}
                    </div>
                    </div>
                )}

                {/* ── The decision, outside the scroller ──
                    Scraped descriptions run long, so buttons at the end of the
                    text were below the fold on most offers: the reviewer had to
                    scroll to the bottom to act, or scrolled back up and moved on
                    without deciding. The bar sits in the pane's flex column
                    instead, so it is on screen wherever the text is. */}
                {selected && (
                    <div className={cls(
                        "shrink-0 border-t bg-surface-sheet",
                        defaultBorderMixin
                    )}>
                        <div className="max-w-4xl mx-auto w-full px-6 py-3 flex flex-wrap items-center gap-2">
                            {selected.status === "approved" ? (
                                <>
                                    <Chip size="small" colorScheme="greenLighter">
                                        <CheckIcon size={iconSize.smallest} /> Publicada en el tablón
                                    </Chip>
                                    {selected.promoted_job_id && (
                                        <>
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => sidePanel.open({
                                                    path: "jobs",
                                                    entityId: selected.promoted_job_id!
                                                })}
                                            >
                                                Ver la vacante
                                            </Button>
                                            {/* The record and the page are not
                                                the same check. The first says
                                                what the LLM wrote; only the
                                                second says whether the board
                                                renders it — logo resolved,
                                                markdown as prose, salary block
                                                present — which is the last
                                                thing worth looking at right
                                                after approving. */}
                                            <Button
                                                component="a"
                                                variant="text"
                                                size="small"
                                                href={publicJobUrl(selected.promoted_job_id)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <ExternalLinkIcon size={iconSize.smallest} /> Verla en el tablón
                                            </Button>
                                        </>
                                    )}
                                </>
                            ) : selected.status === "rejected" || selected.status === "expired" ? (
                                // A closed verdict, and the way out of it. Offering
                                // "Rechazar" again here would be a button that does
                                // nothing, and without a way back a misclick in the
                                // pending queue is permanent.
                                <>
                                    <Chip size="small" colorScheme="grayLighter">
                                        {selected.status === "rejected" ? "Rechazada" : "Caducada"}
                                    </Chip>
                                    <Button
                                        size="small"
                                        variant="text"
                                        disabled={working}
                                        onClick={() => void setStatus(selected, "pending")}
                                    >
                                        <UndoIcon size={iconSize.smallest} /> Devolver a revisión
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={working}
                                        onClick={() => setApproving(selected)}
                                    >
                                        <CheckIcon size={iconSize.smallest} /> Aprobar y publicar
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button size="small" disabled={working} onClick={() => setApproving(selected)}>
                                        <CheckIcon size={iconSize.smallest} /> Aprobar y publicar
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={working}
                                        onClick={() => void setStatus(selected, "rejected")}
                                    >
                                        <ThumbsDown size={iconSize.smallest} /> Rechazar
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="text"
                                        disabled={working}
                                        onClick={() => void setStatus(selected, "expired")}
                                    >
                                        Marcar caducada
                                    </Button>
                                    <Typography
                                        variant="body2"
                                        color="secondary"
                                        className="grow text-right hidden lg:block"
                                    >
                                        Verás lo que se va a publicar antes de confirmarlo.
                                    </Typography>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ApprovePublishDialog
                open={approving !== null}
                client={client}
                scrapedId={approving?.id ?? null}
                scrapedTitle={approving?.title ?? null}
                onClose={() => setApproving(null)}
                onPublished={(body) => { if (approving) onPublished(approving, body); }}
            />
        </div>
    );
}
